// State of the SCI prospecting list (filters, search, sort, pagination), kept in the URL so that
// coming back from a SCI or copro page, a refresh or a shared link shows the same view.
// The API side is src/Service/SCI/SCIListFilters.php.

export type YesNoFilter = 'all' | 'yes' | 'no';
export type ManagedFilter = YesNoFilter | 'unknown';
export type SortDirection = 'asc' | 'desc';

export interface SciListState {
  search: string;
  qualified: YesNoFilter;
  targeted: YesNoFilter;
  targetedTransaction: YesNoFilter;
  alreadyManaged: ManagedFilter;
  // Lot bounds as typed: an invalid value is kept in the URL but not sent to the API
  lotsCoproMin: string;
  lotsCoproMax: string;
  lotsTotalMin: string;
  lotsTotalMax: string;
  sortField: string;
  sortDirection: SortDirection;
  page: number; // 0-based
  rowsPerPage: number;
}

export const DEFAULT_SCI_LIST_STATE: SciListState = {
  search: '',
  qualified: 'all',
  targeted: 'all',
  targetedTransaction: 'all',
  alreadyManaged: 'all',
  lotsCoproMin: '',
  lotsCoproMax: '',
  lotsTotalMin: '',
  lotsTotalMax: '',
  sortField: '',
  sortDirection: 'asc',
  page: 0,
  rowsPerPage: 10,
};

export const SCI_SORT_FIELDS = [
  'denominationUniteLegale',
  'stats.propertiesWithinCopro',
  'stats.totalProperties',
  'qualification.contactQualified',
  'qualification.targetedGestion',
  'qualification.targetedTransaction',
  'qualification.alreadyManaged',
];

// Same options as MUITable's rows-per-page select
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

// URL parameter of each state key
const URL_KEYS: Record<keyof SciListState, string> = {
  search: 'q',
  qualified: 'contact',
  targeted: 'gestloc',
  targetedTransaction: 'transac',
  alreadyManaged: 'engestion',
  lotsCoproMin: 'lotsCoproMin',
  lotsCoproMax: 'lotsCoproMax',
  lotsTotalMin: 'lotsTotalMin',
  lotsTotalMax: 'lotsTotalMax',
  sortField: 'tri',
  sortDirection: 'sens',
  page: 'page',
  rowsPerPage: 'parPage',
};

const FILTER_KEYS: (keyof SciListState)[] = [
  'search', 'qualified', 'targeted', 'targetedTransaction', 'alreadyManaged',
  'lotsCoproMin', 'lotsCoproMax', 'lotsTotalMin', 'lotsTotalMax',
];

const oneOf = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T => (
  value !== null && (allowed as readonly string[]).includes(value) ? value as T : fallback
);

const lotsDraft = (value: string | null): string => (value ?? '').trim().slice(0, 6);

export const parseSciListState = (params: URLSearchParams): SciListState => {
  const page = Number.parseInt(params.get(URL_KEYS.page) ?? '', 10);
  const rowsPerPage = Number.parseInt(params.get(URL_KEYS.rowsPerPage) ?? '', 10);

  return {
    search: (params.get(URL_KEYS.search) ?? '').trim(),
    qualified: oneOf(params.get(URL_KEYS.qualified), ['all', 'yes', 'no'] as const, 'all'),
    targeted: oneOf(params.get(URL_KEYS.targeted), ['all', 'yes', 'no'] as const, 'all'),
    targetedTransaction: oneOf(params.get(URL_KEYS.targetedTransaction), ['all', 'yes', 'no'] as const, 'all'),
    alreadyManaged: oneOf(params.get(URL_KEYS.alreadyManaged), ['all', 'yes', 'no', 'unknown'] as const, 'all'),
    lotsCoproMin: lotsDraft(params.get(URL_KEYS.lotsCoproMin)),
    lotsCoproMax: lotsDraft(params.get(URL_KEYS.lotsCoproMax)),
    lotsTotalMin: lotsDraft(params.get(URL_KEYS.lotsTotalMin)),
    lotsTotalMax: lotsDraft(params.get(URL_KEYS.lotsTotalMax)),
    sortField: oneOf(params.get(URL_KEYS.sortField), ['', ...SCI_SORT_FIELDS], ''),
    sortDirection: oneOf(params.get(URL_KEYS.sortDirection), ['asc', 'desc'] as const, 'asc'),
    // The URL page is 1-based, as shown in the table
    page: Number.isFinite(page) && page > 1 ? page - 1 : 0,
    rowsPerPage: ROWS_PER_PAGE_OPTIONS.includes(rowsPerPage) ? rowsPerPage : DEFAULT_SCI_LIST_STATE.rowsPerPage,
  };
};

/** Only the values that differ from the defaults, so that the default view has a clean URL. */
export const serializeSciListState = (state: SciListState): URLSearchParams => {
  const params = new URLSearchParams();
  (Object.keys(URL_KEYS) as (keyof SciListState)[]).forEach((key) => {
    const value = state[key];
    if (value === DEFAULT_SCI_LIST_STATE[key] || (key === 'sortDirection' && !state.sortField)) {
      return;
    }
    params.set(URL_KEYS[key], key === 'page' ? String(Number(value) + 1) : String(value));
  });

  return params;
};

const parseBound = (value: string): number | null | 'invalid' => {
  if (value.trim() === '') {
    return null;
  }
  if (!/^\d+$/.test(value.trim())) {
    return 'invalid';
  }
  const bound = Number.parseInt(value, 10);

  return bound >= 1 ? bound : 'invalid';
};

export interface LotsRange {
  min: number | null;
  max: number | null;
  error: string | null;
}

/** Inclusive lot range; `error` is set (and the range ignored) when a bound is not a whole number ≥ 1 or min > max. */
export const parseLotsRange = (minValue: string, maxValue: string): LotsRange => {
  const min = parseBound(minValue);
  const max = parseBound(maxValue);
  if (min === 'invalid' || max === 'invalid') {
    return { min: null, max: null, error: 'Nombre entier à partir de 1' };
  }
  if (min !== null && max !== null && min > max) {
    return { min: null, max: null, error: 'Le minimum dépasse le maximum' };
  }

  return { min, max, error: null };
};

/** Query string of /api/companies/{id}/scis (and /kpis without pagination nor sort). */
export const buildSciApiParams = (state: SciListState, options: { pagination: boolean }): URLSearchParams => {
  const params = new URLSearchParams();
  if (options.pagination) {
    params.set('limit', String(state.rowsPerPage));
    params.set('step', String(state.page + 1));
    if (state.sortField) {
      params.set('sortField', state.sortField);
      params.set('sortDirection', state.sortDirection);
    }
  }
  if (state.qualified !== 'all') { params.set('filterQualified', state.qualified); }
  if (state.targeted !== 'all') { params.set('filterTargetedGestion', state.targeted); }
  if (state.targetedTransaction !== 'all') { params.set('filterTargetedTransaction', state.targetedTransaction); }
  if (state.alreadyManaged !== 'all') { params.set('filterAlreadyManaged', state.alreadyManaged); }

  const lotsCopro = parseLotsRange(state.lotsCoproMin, state.lotsCoproMax);
  if (lotsCopro.min !== null) { params.set('filterLotsCoproMin', String(lotsCopro.min)); }
  if (lotsCopro.max !== null) { params.set('filterLotsCoproMax', String(lotsCopro.max)); }
  const lotsTotal = parseLotsRange(state.lotsTotalMin, state.lotsTotalMax);
  if (lotsTotal.min !== null) { params.set('filterLotsTotalMin', String(lotsTotal.min)); }
  if (lotsTotal.max !== null) { params.set('filterLotsTotalMax', String(lotsTotal.max)); }

  if (state.search) { params.set('filterSearch', state.search); }

  return params;
};

/** Number of active filters (a lot range counts once), shown on the reset button. */
export const countActiveFilters = (state: SciListState): number => {
  const active = FILTER_KEYS.filter((key) => state[key] !== DEFAULT_SCI_LIST_STATE[key]);
  const ranges = [['lotsCoproMin', 'lotsCoproMax'], ['lotsTotalMin', 'lotsTotalMax']];

  return active.length - ranges.filter(([min, max]) => active.includes(min as keyof SciListState) && active.includes(max as keyof SciListState)).length;
};

// Last view of the list in this tab, per company, restored when the page is opened from the menu.
const storageKey = (companyId: number) => `sci-overall-view:${companyId}`;

export const readStoredSciListView = (companyId: number): string | null => {
  try {
    return window.sessionStorage.getItem(storageKey(companyId));
  } catch {
    return null;
  }
};

export const storeSciListView = (companyId: number, search: string): void => {
  try {
    if (search) {
      window.sessionStorage.setItem(storageKey(companyId), search);
    } else {
      window.sessionStorage.removeItem(storageKey(companyId));
    }
  } catch {
    // Storage unavailable (private mode): the URL still keeps the view
  }
};
